import { defineStore } from "pinia";
import { ref, computed, watch } from "vue";
import { deriveTakenState } from "../services/domainFlow";
import { injectConsultationPlan } from "../services/planInjectionService";
import { DomainSummaryModel } from "../models/DomainSummaryModel";
import { DomainFlowState } from "../types/DomainFlowState";
import { DomainCheckAvailabilityDto } from "@/features/domain-checker-input/dtos/DomainCheckAvailabilityDto";

const STORAGE_KEY = 'domainSelection';

export const useDomainSelectionStore = defineStore("domainSelection", () => {
    // Load persisted state
    const loadStoredState = () => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            return stored ? JSON.parse(stored) : {};
        } catch {
            return {};
        }
    };

    const stored = loadStoredState();

    const domainName = ref(stored.domainName || "");
    const isAvailable = ref(stored.isAvailable || false);
    const flowState = ref<DomainFlowState>(stored.flowState || "");
    const domainPrice = ref(stored.domainPrice || 15.0);
    const belongsToUser = ref<boolean | null>(stored.belongsToUser || null);
    const consultation = ref(stored.consultation || false);
    const domainType = computed(() => isAvailable.value ? "register" : "transfer")

    function setFromDto(dto: DomainCheckAvailabilityDto) {
        domainName.value = dto.domainName;
        isAvailable.value = dto.available;
        belongsToUser.value = dto.userOwns ?? null;
        flowState.value = dto.available
            ? "available"
            : deriveTakenState(dto.userOwns ?? null, dto.transferRecommended ?? null);
    }

    function defer(withConsultation = false) {
        domainName.value = "";
        isAvailable.value = false;
        consultation.value = withConsultation;
        flowState.value = withConsultation
            ? "deferred-with-consultation"
            : "deferred";
    }

    function reset() {
        domainName.value = "";
        isAvailable.value = false;
        belongsToUser.value = null;
        flowState.value = "none";
        consultation.value = false;
    }

    const summaryItem = computed(() =>
        DomainSummaryModel.create({
            state: flowState.value,
            domainName: domainName.value,
            price: domainPrice.value,
        })
    );

    const injectedPlan = computed(() =>
        injectConsultationPlan(flowState.value)
    );

    // Persist state changes
    const persistState = () => {
        const state = {
            domainName: domainName.value,
            isAvailable: isAvailable.value,
            flowState: flowState.value,
            domainPrice: domainPrice.value,
            belongsToUser: belongsToUser.value,
            consultation: consultation.value,
            domainType: domainType.value,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    };

    // Watch for changes and persist
    watch([domainName, isAvailable, flowState, domainPrice, belongsToUser, consultation, domainType], persistState, { deep: true });

    return {
        domainName,
        domainType,
        isAvailable,
        flowState,
        domainPrice,
        belongsToUser,
        consultation,
        setFromDto,
        defer,
        reset,
        summaryItem,
        injectedPlan,
    };
});